import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
  Chip
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { brandColors } from '../../theme/theme';
import {
  getUserCompany,
  getCompanyUsers,
  removeUserFromCompany
} from '../../lib/services/enterpriseCompanyService';

/**
 * EnterpriseCompanyUsers Page
 *
 * Manage enterprise company users for Enterprise Admins
 */
const EnterpriseCompanyUsers = () => {
  const { currentUser } = useAuth();
  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Invite dialog state
  const [inviteDialog, setInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const companyData = await getUserCompany(currentUser.uid);
      
      if (!companyData) {
        setError('No enterprise company found for this user');
        return;
      }
      
      setCompany(companyData);
      
      const companyUsers = await getCompanyUsers(companyData.id);
      setUsers(companyUsers);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load enterprise company users');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInvite = async () => {
    if (!inviteEmail || !company) return;
    
    try {
      setInviting(true);
      setError(null);
      
      // TODO: In production, this would check if user exists or send invitation email
      // For now, we'll just show a message
      alert(`Invitation feature coming soon!\n\nEmail: ${inviteEmail}\n\nIn production, this will:\n1. Check if user exists\n2. If exists: Add to company immediately\n3. If new: Send invitation email with signup link`);
      
      setInviteDialog(false);
      setInviteEmail('');
      fetchData();
    } catch (err) {
      console.error('Error inviting user:', err);
      setError(`Failed to invite user: ${err.message}`);
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (user) => {
    if (!window.confirm(`Remove ${user.email} from ${company.companyName}?`)) {
      return;
    }
    
    try {
      await removeUserFromCompany(company.id, user.id);
      alert('User removed successfully');
      fetchData();
    } catch (err) {
      console.error('Error removing user:', err);
      alert(`Failed to remove user: ${err.message}`);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const canAddUsers = () => {
    return company && company.usedSeats < company.totalSeats;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error || !company) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {error || 'Enterprise company not found'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Enterprise Company Users
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {company.companyName}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setInviteDialog(true)}
            disabled={!canAddUsers()}
            sx={{ bgcolor: brandColors.primary }}
          >
            Invite User
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Seat Usage Alert */}
      {!canAddUsers() && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          All seats are currently in use ({company.usedSeats}/{company.totalSeats}). 
          Contact support to add more seats.
        </Alert>
      )}

      {/* Users Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Active Users ({users.length} of {company.totalSeats})
            </Typography>
            <Chip
              label={`${company.totalSeats - company.usedSeats} seats available`}
              color={canAddUsers() ? 'success' : 'error'}
              size="small"
            />
          </Box>
          
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Added</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {user.displayName || 'No Name'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {user.isFilmEnterpriseAdmin ? (
                        <Chip
                          icon={<AdminIcon />}
                          label="Admin"
                          color="primary"
                          size="small"
                        />
                      ) : (
                        <Chip
                          label="User"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(user.addedToFilmCompanyAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(user.lastLogin)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Remove from Company">
                        <IconButton
                          size="small"
                          onClick={() => handleRemove(user)}
                          color="error"
                          disabled={user.isFilmEnterpriseAdmin} // Can't remove admins
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {users.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No users yet
              </Typography>
              <Button
                variant="outlined"
                startIcon={<PersonAddIcon />}
                onClick={() => setInviteDialog(true)}
                sx={{ mt: 2 }}
              >
                Invite First User
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Info Box */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Note:</strong> Enterprise admins cannot be removed through this interface. 
          To modify admin roles, please contact support.
        </Typography>
      </Alert>

      {/* Invite Dialog */}
      <Dialog open={inviteDialog} onClose={() => setInviteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite User to Company</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="user@example.com"
              helperText="User will receive an invitation email to join your company"
            />
            
            <Alert severity="info" sx={{ mt: 2 }}>
              Available seats: {company.totalSeats - company.usedSeats} of {company.totalSeats}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleInvite} 
            variant="contained"
            disabled={!inviteEmail || inviting || !canAddUsers()}
          >
            {inviting ? <CircularProgress size={20} /> : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EnterpriseCompanyUsers;

